import ServiceBase from '../ServiceBase';

export default class UsersAdminService extends ServiceBase {
    addUserCreditHistoryItem(item) {
        const params = {
            userID: null,
            amount: null,
            reason: null,
            ...item,
        };

        return this.request('post', 'admin/users/addUserCreditHistoryItem', params);
    }

    addOrUpdateProjectContextRoles(userID, creatorID, projectID, roleFlags) {
        return this.request('post', 'admin/users/addOrUpdateProjectContextRoles', { userID, creatorID, projectID, roleFlags });
    }

    addOrUpdateProjectContextQuirks(userID, creatorID, projectID, quirkFlags) {
        return this.request('post', 'admin/users/addOrUpdateProjectContextQuirks', { userID, creatorID, projectID, quirkFlags });
    }

    createUserGroup(userGroup) {
        const params = {
            ...userGroup,
        };

        return this.request('post', 'admin/users/createUserGroup', params);
    }

    deleteUser(userID) {
        const params = {
            userID,
        };

        return this.request('post', 'admin/users/deleteUser', params);
    }

    deleteUserGroup(userGroupID, projectID) {
        const params = {
            userGroupID,
            projectID,
        };

        return this.request('post', 'admin/users/deleteUserGroup', params);
    }

    exportUsers(userIDs, creatorID, projectID, filters) {
        const params = Object.assign({
            userIDs,
            creatorID,
            projectID,
            fullName: null,
            email: null,
            status: null,
        }, filters);

        return this.request('post', 'admin/users/exportUsers', params);
    }

    getRoleFlagsEditableAccess(creatorID, projectID) {
        return this.request('get', 'admin/users/getRoleFlagsEditableAccess', { creatorID, projectID });
    }

    getUserDetails(userID, projectContext) {
        return this.request('get', 'admin/users/getUserDetails', { userID, creatorID: projectContext.creatorID, projectID: projectContext.projectID });
    }

    getUserIDs(filters, creatorID, projectID) {
        const params = {
            fullName: null,
            email: null,
            status: null,
            creatorID,
            projectID,
            ...filters,
        };

        return this.request('post', 'admin/users/getUserIDs', params);
    }

    getUserOrders(userID, creatorID, projectID) {
        return this.request('get', 'admin/users/getUserOrders', { userID, creatorID, projectID });
    }

    getImportUsersExampleFile() {
        return this.$http.get('admin/users/getImportUsersExampleFile', { responseType: 'blob' });
    }

    getUserCreditHistoryItems(userID, projectID) {
        return this.request('get', 'admin/users/getUserCreditHistoryItems', { userID, projectID });
    }

    getUserJoinProjectAddress(userID, projectID) {
        return this.request('get', 'admin/users/getUserJoinProjectAddress', { userID, projectID });
    }

    getUserRoleFlagDefinitions() {
        return this.request('get', 'admin/users/getUserRoleFlagDefinitions');
    }

    getUserQuirkFlagDefinitions() {
        return this.request('get', 'admin/users/getUserQuirkFlagDefinitions');
    }

    getWelcomeEmailRecipients(projectID) {
        return this.request('get', 'admin/users/getWelcomeEmailRecipients', { projectID });
    }

    impersonateUser(userID) {
        return this.request('post', 'admin/users/impersonateUser', { userID });
    }

    importUsers(sourceFile, projectID, defaultUserGroupID, markUsersAsOriginalBackers) {
        const formData = new FormData();
        formData.append('sourceFile', sourceFile);
        formData.append('projectID', projectID);
        formData.append('defaultUserGroupID', defaultUserGroupID);
        formData.append('markUsersAsOriginalBackers', markUsersAsOriginalBackers);

        return this.request('post', 'admin/users/importUsers', formData);
    }

    inviteCollaborators(emails, role) {
        return this.request('post', 'admin/users/inviteCollaborators', { emails, role });
    }

    inviteCollaboratorsToProject(emails, role, projectID) {
        return this.request('post', 'admin/users/inviteCollaboratorsToProject', { emails, role, projectID });
    }

    removeUsersFromProject(projectID, userIDs, keepCredits) {
        return this.request('post', 'admin/users/removeUsersFromProject', { projectID, userIDs, keepCredits });
    }

    searchUserGroups(projectID, filters = {}, pageSize = null, pageIndex = null) {
        const params = {
            projectID,
            pageSize,
            pageIndex,
            ...filters,
        };

        return this.request('post', 'admin/users/searchUserGroups', params);
    }

    loadUserGroupsAssignment(userIDs, projectID) {
        return this.request('post', 'admin/users/loadUserGroupsAssignment', { userIDs, projectID });
    }

    updateUserBadge(userID, userBadgeType, isGranted) {
        const params = {
            userID,
            userBadgeType,
            isGranted,
        };

        return this.request('post', 'admin/users/updateUserBadge', params);
    }

    updateUserGroupsAssignment(assignment) {
        return this.request('post', 'admin/users/updateUserGroupsAssignment', assignment);
    }

    updateUserDetails(userDetails) {
        return this.request('post', 'admin/users/updateUserDetails', userDetails);
    }

    updateUserGroup(userGroup) {
        const params = {
            ...userGroup,
        };

        return this.request('post', 'admin/users/updateUserGroup', params);
    }

    updateUserVerifiedAccountCaption(userID, verifiedAccountCaption) {
        return this.request('post', 'admin/users/updateUserVerifiedAccountCaption', { userID, verifiedAccountCaption });
    }

    searchUsers(filters, creatorID, projectID, pageSize, pageIndex) {
        const params = {
            fullName: null,
            email: null,
            status: null,
            creatorID,
            projectID,
            pageSize,
            pageIndex,
            ...filters,
        };

        return this.request('post', 'admin/users/searchUsers', params);
    }

    sendWelcomeNotifications(userIDs, projectID) {
        return this.request('post', 'admin/users/sendWelcomeNotifications', { userIDs, projectID });
    }

    sendWelcomeNotification(userID, projectID) {
        return this.request('post', 'admin/users/sendWelcomeNotification', { userID, projectID });
    }

    getContentModerators() {
        return this.request('get', 'admin/users/getContentModerators');
    }

    InviteUsersToProject(projectID, { userGroupID, relationType, emails } ) {
        const params = {
            projectID,
            userGroupID,
            relationType,
            emails,
        };
        return this.request('post', 'admin/users/InviteUsersToProject', params);
    }

    getProjectsForUserGroupsAssociation(userID) {
        return this.request('get', 'admin/users/getProjectsForUserGroupsAssociation', { userID });
    }

    getProjectUserGroupsAssociations(userID, projectID) {
        return this.request('get', 'admin/users/getProjectUserGroupAssociations', { userID, projectID });
    }
}